import gql from 'graphql-tag';
import { ModalDatasourcesFolderId } from './apiConstant';

const TABULAR_DATA_QUERY_FRAGMENTS = `
  fragment TabularDataFragment on TabularData {
    L1Sections: children {
      ... on DataSection {
        ...L1SectionFragment
      }
    }
  }
  fragment L1SectionFragment on DataSection {
    L1Heading: label {
      value
    }
    L1ShortLabel: shortLabel {
      value
    }
    sortOrder:field(name:"__sortOrder") {value}
    selectedDataPoints {
    targetItems {...DataPointFragment}
    }
    L2Sections: children(includeTemplateIDs: ["{49D5F8D9-2685-47B0-B9B7-814D25D0CC50}"]) {
      ...L2SectionFragment
    }
  }

  fragment L2SectionFragment on DataSection {
    L2Heading: label {
      value
    }
    L2ShortLabel: shortLabel {
      value
    }
    hideFor {
      targetItems {
        ... HideForVehicleType
      }
    }
    sortOrder:field(name:"__sortOrder") {value}
    selectedDataPoints {
    targetItems {...DataPointFragment}
    }
  }

  fragment DataPointFragment on DataPoint {    
    label {
      value
    }
    amsField {
      value
    }
    hideFor {
      targetItems {
        ... HideForVehicleType
      }
    }
    selectedDataTransforms {
      targetItems {
        ...DataTransformFunction
      }
    }
  }

  fragment DataTransformFunction on DataTransformFunction {
    function {value}
  }

  fragment HideForVehicleType on VehicleType {
    vehicleType {
      value
    }
  }
`;

export const SEARCH_RESULTS_DS_QUERY = gql`
  query SEARCH_RESULTS_DS_QUERY($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      ... on SearchResults {
        moduleTitle {
          value
        }
        numberOfTilesToFetch {
          value
        }
        showMoreText {
          value
        }
        noMoreResultsText {
          value
        }
        noResultsFoundHeadingText {
          value
        }
        noResultsFoundDescriptionText {
          value
        }
        noResultsFoundStartNewSearchText {
          value
        }
        noResultsFoundRefineSearchText {
          value
        }
        noResultsFoundStartNewSearchButtonText {
          value
        }
      }
    }
  }
`;

export const SEARCH_WIDGET_DS_QUERY = gql`
  query SEARCH_WIDGET_DS_QUERY($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      ...SearchWidgetFragment
    }
  }

  fragment SearchWidgetFragment on SearchWidget {
    moduleTitle {
      value
    }
    searchButtonText {
      value
    }
    defaultSearchType {
      targetItem {
        listValue: field(name: "listValue") {
          value
        }
        listLabel: field(name: "listLabel") {
          value
        }
      }
    }
    searchTypes {
      targetItems {
        listValue: field(name: "listValue") {
          value
        }
        listLabel: field(name: "listLabel") {
          value
        }
      }
    }
  }
`;

export const COMPARE_VEHICLES_DS_QUERY = gql`
  query ($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      ... on _TabularDatasource {
        tabularData {
          targetItem {
            ...TabularDataFragment
          }
        }
      }
      ... on CompareVehicles {
        moduleTitle {
          value
        }
      }
    }
  }

  ${TABULAR_DATA_QUERY_FRAGMENTS}
`;

export const SHARE_COPY_DS_QUERY = gql`
  query SHARE_COPY_DS_QUERY($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      ... on ShareCopy {
        copyLinkText {
          value
        }
        shareLinkText {
          value
        }
        emailSubject {
          value
        }
        emailBody {
          value
        }
      }
    }
  }
`;

export const COMPARISON_FOOTER_DS_QUERY = gql`
  query COMPARISON_FOOTER_DS_QUERY($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      ... on ComparisonFooter {
        viewComparisonButtonText {
          value
        }
        comparisonTextPrefix {
          value
        }
        comparisonTextSuffix {
          value
        }
        closeButtonText {
          value
        }
        carTilePlaceholderText {
          value
        }
        carLoadingText {
          value
        }
      }
    }
  }
`;

export const MAKE_MODEL_MODAL_DS_QUERY = gql`
  query MAKE_MODEL_MODAL_DS_QUERY($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      ... on MakeModelModal {
        searchPlaceholder {
          value
        }
        searchOptionHeading {
          value
        }
        searchButtonText {
          value
        }
        clearOptionText {
          value
        }
      }
    }
  }
`;

export const FUEL_PRICE_DS_QUERY = gql`
  query FUEL_PRICE_DS_QUERY($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      children {
        ... on FuelPrice {
          fuelType {
            targetItem {
              product: field(name: "listValue") {
                value
              }
            }
          }
          fuelPrice {
            value
          }
        }
      }
    }
  }
`;

export const REGISTRATION_COSTS_DS_QUERY = gql`
  query REGISTRATION_COSTS_DS_QUERY($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      children {
        ... on RegistrationCost {
          registrationCost {
            value
          }
          cylinders {
            value
          }
        }
      }
    }
  }
`;

export const CAR_DETAILS_DS_QUERY = gql`
  query ($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      ... on CarDetails {
        noVehicleFoundHeadingText {
          value
        }
        noVehicleFoundDescriptionText {
          value
        }
        noVehicleFoundStartNewSearchText {
          value
        }
        noVehicleFoundStartNewSearchClosingText {
          value
        }
        noVehicleFoundStartNewSearchButtonText {
          value
        }
        # Key stats labels
        configuration {
          value
        }
        fuel {
          value
        }
        emissionsRating {
          value
        }
        costToFuelRecharge {
          value
        }

        costToFillLabel {
          value
        }
        costPer100kmLabel {
          value
        }
        registrationLabel {
          value
        }

        batteriesLabel {
          value
        }
        batteriesLink {
          jss
        }
        insuranceLabel {
          value
        }
        insuranceLink {
          jss
        }
        roadsideLabel {
          value
        }
        roadsideLink {
          jss
        }
      }
    }
  }
`;

export const TABULAR_DATA_DS_QUERY = gql`
  query ($datasource: String) {
    datasource: item(path: $datasource, language: "en") {
      ... on _TabularDatasource {
        tabularData {
          targetItem {
            ...TabularDataFragment
          }
        }
      }
    }
  }
  ${TABULAR_DATA_QUERY_FRAGMENTS}
`;

export const ALL_MODALS_DS_QUERY = gql`
  query ALL_MODALS_DS_QUERY {
    datasource: item(path: "${ModalDatasourcesFolderId}", language: "en") {
      children {
        name
        id
        ... on ModalDatasource {
          key {
            value
          }
          title {
            value
          }
          description {
            value
          }
        }
      }
    }
  }
`;
